import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import EditNoteIcon from '@mui/icons-material/EditNote';
import HelpIcon from '@mui/icons-material/Help';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import { RequestType } from '@shared/interfaces/CommonInterfaces';
import React from 'react';

type RequestStatusChipProps = {
  status?: string;
  type?: RequestType;
  description?: string;
  trailingIcon?: React.ReactNode;
  onClick?: () => void;
  loading?: boolean;
}

function RequestStatusChip(props: RequestStatusChipProps) {
  const type = props.type || "unknown";
  const status = props.status || "unknown";
  const description = props.description || "";
  const statusInfo: { [key in (RequestType | "unknown")]: any } = {
    create: {
      new: { color: "primary", icon: EditNoteIcon, label: "New" },
      pending: { color: "primary", icon: WatchLaterIcon, label: "Create pending" },
      completed: { color: "success", icon: CheckCircleIcon, label: "Created" },
      failed: { color: "success", icon: CheckCircleIcon, label: "Created" },
      cancelled: { color: "error", icon: CancelIcon, label: "Create cancelled" },
    },
    import: {
      new: { color: "primary", icon: EditNoteIcon, label: "Import requested" },
      pending: { color: "primary", icon: WatchLaterIcon, label: "Import pending" },
      completed: { color: "success", icon: CheckCircleIcon, label: "Imported" },
      failed: { color: "success", icon: CheckCircleIcon, label: "Imported" },
      cancelled: { color: "error", icon: CancelIcon, label: "Import cancelled" },
    },
    modify: {
      new: { color: "primary", icon: EditNoteIcon, label: "Modify requested" },
      pending: { color: "primary", icon: WatchLaterIcon, label: "Modify pending" },
      completed: { color: "success", icon: CheckCircleIcon, label: "Modified" },
      failed: { color: "success", icon: CheckCircleIcon, label: "Modified" },
      cancelled: { color: "error", icon: CancelIcon, label: "Modify cancelled" },
    },
    delete: {
      new: { color: "primary", icon: EditNoteIcon, label: "Delete requested" },
      pending: { color: "primary", icon: WatchLaterIcon, label: "Delete pending" },
      completed: { color: "default", icon: DeleteIcon, label: "Deleted" },
      failed: { color: "default", icon: DeleteIcon, label: "Deleted" },
      cancelled: { color: "error", icon: CancelIcon, label: "Delete cancelled" },
    },
    unknown: {
      unknown: { color: "default", icon: HelpIcon, label: props.onClick ? "Get details ..." : "Not tracked" },
    }
  };

  const statusIcon = statusInfo[type]?.[status]?.icon;

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <Tooltip title={description} placement="bottom" arrow>
        <Chip
          icon={props.loading ? <CircularProgress size={14} sx={{ ml: '6px' }} /> : React.createElement(statusIcon)}
          color={statusInfo[type]?.[status]?.color}
          label={statusInfo[type]?.[status]?.label}
          onClick={props.onClick}
          sx={{
            cursor: props.onClick ? 'pointer' : undefined,
            // Reserve enough space for the trailing icon so it does not overlap the label.
            ...(props.trailingIcon ? { pr: 4.5 } : undefined)
          }}
        />
      </Tooltip>
      {props.trailingIcon && (
        <Box sx={{ position: 'absolute', right: 5, top: '50%', transform: 'translateY(-50%)' }}>
          {props.trailingIcon}
        </Box>
      )}
    </Box>
  );
}

export default RequestStatusChip;
